import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { auth, db } from './firebaseConfig';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, signOut, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import Logo from './components/Logo';
import './LinkedInTheme.css';

function Auth({ setUser }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [referralSource, setReferralSource] = useState(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('invite_token');
    const ref = queryParams.get('ref');
    if (token) {
      setInviteToken(token);
      setIsSignUp(true);
    }
    if (ref) {
      setReferralSource(ref);
    }
  }, [location]);

  const handleLanguageCycle = () => {
    const languages = ['en-GB', 'tr-TR'];
    const currentLanguage = i18n.language;
    let currentIndex = languages.findIndex(lang => currentLanguage.startsWith(lang.substring(0, 2)));
    if (currentIndex === -1) currentIndex = 0;
    const nextIndex = (currentIndex + 1) % languages.length;
    i18n.changeLanguage(languages[nextIndex]);
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError(t('auth.errors.invalidCredential', "Please enter your email address."));
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
      setError(null);
      setTimeout(() => setResetEmailSent(false), 5000);
    } catch (error) {
      console.error("Password reset error:", error);
      let message = error.message;
      switch (error.code) {
        case 'auth/user-not-found':
          message = t('auth.errors.userNotFound', "Account not found. Please sign up to create an account.");
          break;
        case 'auth/invalid-email':
        case 'auth/missing-email':
          message = t('auth.errors.invalidCredential', "Invalid email address.");
          break;
        default:
          message = error.message;
      }
      setError(message);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setResetEmailSent(false);
    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError("Passwords do not match");
          return;
        }

        if (inviteToken) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/create-invited-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invite_token: inviteToken,
              email,
              password,
              fullName,
            }),
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || 'Failed to create invited user.');
          }
          
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          setUser(userCredential.user);

        } else {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;

          await updateProfile(user, {
            displayName: fullName
          });
          
          const actionCodeSettings = {
            url: `${window.location.origin}/welcome`,
          };
          await sendEmailVerification(user, actionCodeSettings);

          await setDoc(doc(db, "users", user.uid), {
            email: user.email,
            displayName: fullName,
            createdAt: serverTimestamp(),
            status: 'active',
            profile: {
              sessionCounter: 1,
              onboardingComplete: false,
              roles: ['candidate'],
              referralSource: referralSource || 'direct',
            },
          });

          await signOut(auth);
          setShowVerificationMessage(true);
        }
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
          setError(t('emailNotVerified'));
          setInfo({
            message: t('resendVerificationPrompt'),
            user: userCredential.user
          });
          await signOut(auth);
          return;
        }
        setUser(userCredential.user);
        if (location.state?.from) {
            navigate(location.state.from);
        }
      }
    } catch (error) {
      // Map Firebase error codes to user-friendly messages
      let message = error.message;
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          message = t('auth.errors.invalidCredential', "Incorrect email or password. Please check your credentials and try again.");
          break;
        case 'auth/user-not-found':
          message = t('auth.errors.userNotFound', "Account not found. Switched to sign up.");
          setIsSignUp(true);
          break;
        case 'auth/email-already-in-use':
          message = t('auth.errors.emailInUse', "This email is already in use.");
          break;
        default:
          message = error.message;
      }
      setError(message);
    }
  };

  const handleResendVerification = async (userToVerify) => {
    if (!userToVerify) return;
    try {
      await sendEmailVerification(userToVerify);
      setInfo({ message: t('verificationResent') });
    } catch (error) {
      setError(error.message);
    }
  };

  if (showVerificationMessage) {
    return (
      <div className="li-auth-wrapper">
        <header className="li-auth-header">
             <div className="li-auth-header-content">
                 <Logo size={30} />
             </div>
        </header>
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', paddingTop: '40px' }}>
           <div className="li-card" style={{ width: '100%', maxWidth: '352px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', borderRadius: '8px', backgroundColor: '#fff', textAlign: 'center' }}>
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>{t('verification.title')}</h2>
              <p style={{ fontSize: '16px', color: 'rgba(0,0,0,0.9)', marginBottom: '24px' }}>{t('verification.message', { email })}</p>
              <button className="li-btn-primary" style={{ width: '100%', height: '48px', borderRadius: '24px' }} onClick={() => {
                setShowVerificationMessage(false);
                setIsSignUp(false);
                setEmail('');
                setPassword('');
              }}>{t('verification.backToLogin')}</button>
           </div>
        </main>
      </div>
    );
  }

  return (
    <div className="li-auth-wrapper">
      
      {/* Header */}
      <header className="li-auth-header">
         <div className="li-auth-header-content">
            <Logo size={34} style={{ color: '#0a66c2' }} />
            
             {/* Action Buttons (Join / Sign In) */}
             <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Language Switcher */}
                  <button 
                      onClick={handleLanguageCycle}
                      style={{ border: 'none', background: 'none', color: '#666', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"></circle><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" fill="none" stroke="currentColor" strokeWidth="2"></path></svg>
                      <span className="hide-on-mobile">{i18n.language.startsWith('tr') ? 'English' : 'Türkçe'}</span>
                  </button>

                 {isSignUp ? (
                     <>
                        <span className="hide-on-mobile" style={{ fontSize: '14px', fontWeight: '600', color: 'rgba(0,0,0,0.6)' }}>{t('auth.alreadyOnPod', 'Already on POD+?')}</span>
                        <button onClick={() => setIsSignUp(false)} className="li-btn-outline-secondary" style={{ borderRadius: '24px', padding: '10px 24px' }}>{t('auth.signIn', 'Sign in')}</button>
                     </>
                 ) : (
                    <>
                       <button onClick={() => setIsSignUp(true)} className="li-btn-primary" style={{ borderRadius: '24px', padding: '10px 24px' }}>{t('auth.joinNow', 'Join now')}</button>
                       <button onClick={() => setIsSignUp(false)} className="li-btn-outline-primary" style={{ borderRadius: '24px', padding: '10px 24px', marginLeft: '8px' }}>{t('auth.signIn', 'Sign in')}</button>
                    </>
                 )}
             </div>
         </div>
      </header>

      {/* Main Hero Content */}
      <main className="li-auth-hero">
        
        {/* Left Content: Text + Form */}
        <div className="li-auth-hero-content">
          <h1 className="li-auth-hero-headline">
             {t('auth.hero.headline', 'Generate Your professional AI Twin.')}
          </h1>

          <div className="li-auth-form-container">
              
            {/* Form toggles based on state but structurally stays here */}
            <div style={{ marginBottom: '24px' }}>
               {info && !info.user && <div className="li-success-msg-small" style={{ marginBottom: '16px' }}>{info.message || info}</div>}
               {info && info.user && (
                   <div className="li-card" style={{ padding: '12px', backgroundColor: '#f3f2ef', border: 'none', marginBottom: '16px' }}>
                       <p style={{ fontSize: '14px', marginBottom: '8px' }}>{info.message}</p>
                       <button className="li-btn-outline-primary" onClick={() => handleResendVerification(info.user)}>
                          {t('resendVerification')}
                       </button>
                   </div>
                )}
                
                {resetEmailSent && (
                  <div className="li-success-msg-small" style={{ marginBottom: '16px' }}>
                    {t('auth.errors.resetPasswordSent', "Password reset email sent!")}
                  </div>
                )}

                <form onSubmit={handleAuth}>
                  {isSignUp && (
                    <div style={{ marginBottom: '12px' }}>
                      <label htmlFor="auth-fullname" style={{ display: 'block', fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '4px' }}>{t('fullName', 'Full Name')}</label>
                      <input
                        id="auth-fullname"
                        className="li-input"
                        type="text"
                        style={{ width: '100%', height: '48px', fontSize: '16px', boxSizing: 'border-box' }}
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '12px' }}>
                       <label htmlFor="auth-email" style={{ display: 'block', fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '4px' }}>{t('email', 'Email')}</label>
                       <input
                          id="auth-email"
                          className="li-input"
                          type="email"
                          style={{ width: '100%', height: '48px', fontSize: '16px', boxSizing: 'border-box' }}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                      <label htmlFor="auth-password" style={{ display: 'block', fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '4px' }}>{t('password', 'Password')}</label>
                      <div style={{ position: 'relative' }}>
                          <input
                              id="auth-password"
                              className="li-input"
                              type="password"
                              style={{ width: '100%', height: '48px', fontSize: '16px', boxSizing: 'border-box' }}
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                            />
                          {!isSignUp && (
                            <button 
                              type="button"
                              onClick={handlePasswordReset}
                              style={{ position: 'absolute', right: 0, top: 0, background: 'none', border: 'none', color: '#0a66c2', fontWeight: '600', fontSize: '12px', cursor: 'pointer' }}
                            >
                              {t('auth.forgotPassword', 'Forgot password?')}
                            </button>
                          )}
                      </div>
                  </div>
                  
                  {/* Confirm Password for Signup */}
                  {isSignUp && (
                      <div style={{ marginBottom: '16px' }}>
                          <label htmlFor="auth-confirm-password" style={{ display: 'block', fontSize: '14px', color: 'rgba(0,0,0,0.6)', marginBottom: '4px' }}>{t('confirmPassword', 'Confirm Password')}</label>
                          <input
                              id="auth-confirm-password"
                              className="li-input"
                              type="password"
                              style={{ width: '100%', height: '48px', fontSize: '16px', boxSizing: 'border-box' }}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              required
                          />
                      </div>
                  )}

                  <div style={{ marginBottom: '16px', marginTop: '24px' }}>
                       <button type="submit" className="li-btn-primary" style={{ width: '100%', height: '48px', fontSize: '16px', borderRadius: '24px', fontWeight: 'bold' }}>
                          {isSignUp ? t('agreeAndJoin', 'Agree & Join') : t('logIn', 'Sign in')}
                      </button>
                  </div>

                  {error && <div className="li-error-msg-small" style={{ marginBottom: '16px' }} role="alert" aria-live="assertive">{error}</div>}
                </form>
                
                 {/* Toggle Link inside form area */}
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                   {isSignUp ? (
                       <span style={{ fontSize: '14px' }}>
                          {t('auth.alreadyOnPod', 'Already on POD+?')} <button onClick={() => setIsSignUp(false)} style={{ border: 'none', background: 'none', color: '#0a66c2', fontWeight: '600', cursor: 'pointer' }}>{t('auth.signIn', 'Sign in')}</button>
                       </span>
                   ) : (
                       <span style={{ fontSize: '14px' }}>
                          {t('auth.newToPod', 'New to POD+?')} <button onClick={() => setIsSignUp(true)} style={{ border: 'none', background: 'none', color: '#0a66c2', fontWeight: '600', cursor: 'pointer' }}>{t('auth.joinNow', 'Join now')}</button>
                       </span>
                   )}
                </div>
            </div>

          </div>
        </div>

        {/* Right Content: Illustration */}
        <div className="li-auth-hero-image">
            <img 
                src="/Flat_illutrations/cat_ladder.png" 
                alt="Welcome to your professional community" 
            />
        </div>
      </main>

      {/* New Sections: Ecosystem & Journey */}
      
      {/* 1. Ecosystem Pills */}
      <section className="li-section">
          <div className="li-section-content">
              <div className="li-section-pills-left">
                  <h2 className="li-section-header">{t('auth.ecosystem.title', 'Explore the Ecosystem')}</h2>
                  <p className="li-section-subhead">{t('auth.ecosystem.subtitle', 'Join the Agent Network System (ANS) where AI agents connect talent with opportunity instantly.')}</p>
              </div>
              <div className="li-section-pills-right">
                  <ul className="li-pill-list">
                      <li><span className="li-pill">{t('auth.ecosystem.pills.digitalTwin', 'AI Twin')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.ans', 'Agent Network System')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.aiRecruiter', 'AI Recruiter')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.simulation', 'Simulation')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.vtc', 'Verified Talent Card')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.cultureFit', 'Team Culture Fit')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.automatedInterviews', 'Automated Interviews')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.hiringManagerAI', 'Hiring Manager AI')}</span></li>
                      <li><span className="li-pill">{t('auth.ecosystem.pills.orchestrator', 'Orchestrator')}</span></li>
                  </ul>
              </div>
          </div>
      </section>

      {/* 2. The Journey (Tiles) */}
      <section className="li-section" style={{ backgroundColor: '#faf9f7' }}>
          <div className="li-section-content" style={{ flexDirection: 'column' }}>
              <h2 className="li-section-header" style={{ textAlign: 'center', width: '100%', marginBottom: '48px' }}>{t('auth.journey.title', 'How the Simulation Works')}</h2>
              
              <div className="li-tile-grid">
                  {/* Step 1 */}
                  <div className="li-tile">
                      <img src="/Flat_illutrations/simulation.png" alt="Create AI Twin" className="li-tile-img" />
                      <div className="li-tile-content">
                          <h3 className="li-tile-title">{t('auth.journey.step1.title', '1. AI Twin Generation')}</h3>
                          <p className="li-tile-text">{t('auth.journey.step1.text', 'Candidates and Hiring Managers create their AI counterparts (AI Twins) to represent them in the virtual world.')}</p>
                      </div>
                  </div>

                  {/* Step 2 */}
                  <div className="li-tile">
                      <img src="/Flat_illutrations/diverse_group_in_garden.png" alt="AI Simulation" className="li-tile-img" />
                      <div className="li-tile-content">
                          <h3 className="li-tile-title">{t('auth.journey.step2.title', '2. AI Simulation')}</h3>
                          <p className="li-tile-text">{t('auth.journey.step2.text', "The system orchestrates a multi-stage interview simulation where your AI Twin engages with the hiring team's twins.")}</p>
                      </div>
                  </div>

                  {/* Step 3 */}
                  <div className="li-tile">
                      <img src="/Flat_illutrations/report.png" alt="Actionable Insights" className="li-tile-img" />
                      <div className="li-tile-content">
                          <h3 className="li-tile-title">{t('auth.journey.step3.title', '3. Actionable Insights')}</h3>
                          <p className="li-tile-text">{t('auth.journey.step3.text', 'Receive detailed reports and verified shortlists, giving you deep insights into technical skills and cultural fit before you even meet.')}</p>
                      </div>
                  </div>
              </div>
          </div>
      </section>

      {/* 3. Talent Finder CTA */}
      <div className="li-cta-banner">
           <h2 className="li-cta-header">{t('auth.cta.title', 'Ready to create your AI Twin?')}</h2>
           <Link to="/profile" className="li-btn-primary" style={{ fontSize: '18px', padding: '12px 32px', borderRadius: '28px', textDecoration: 'none', display: 'inline-block' }}>
               {t('auth.cta.button', 'Start Chat')}
           </Link>
      </div>
      
      {/* Footer */}
      <footer className="footer" style={{ marginTop: 'auto', padding: '24px', textAlign: 'center', fontSize: '12px', color: 'rgba(0,0,0,0.6)', width: '100%' }}>
         <div style={{ maxWidth: '1128px', margin: '0 auto' }}>
            <p>{t('auth.footer.rights', '© 2025 POD+. All rights reserved.')}</p>
         </div>
      </footer>
    </div>
  );
}

export default Auth;